'use client';

import type { FC } from 'react';
import type { GlobalSearchResponseDataType, GlobalSearchResponseItemType } from '@weberus/shared-types';
import type { HomeLogPageNameValue } from '@/app/(pages)/sub-home/[category]/constants';
import type { WbSession } from '@weberus/session';
import { useMemo, useEffect } from 'react';
import { waitUntilClickTimeout } from '@weberus/utils';
import { clientLogStandardized } from '@weberus/client-tracker';
import { useInView } from 'react-intersection-observer';
import { addDays, differenceInDays, format } from 'date-fns';
import { navigateTo } from '@/app/_shared/utils/action-navigator';
import { GlobalSearchMobile } from '@/app/_components/widgets/global-search/ui/global-search-mobile';
import { GlobalSearchPC } from '@/app/_components/widgets/global-search/ui/global-search-pc';

/**
 * 해외숙소 검색
 * @see {@link https://www.figma.com/design/jImqz2em0kItEVBBffwz03/%5BGUIDE%5D-%ED%99%88%2F%EC%84%9C%EB%B8%8C%ED%99%88?node-id=4868-95780&t=jNiAtH0flhWFfno2-1}
 */

export interface GlobalSearchProps {
  data?: GlobalSearchResponseDataType;
  pageNameLog: HomeLogPageNameValue;
  globalPlaceFilter?: WbSession['globalPlaceFilter'];
}

const DEFAULT_PLACEHOLDER = '검색어를 입력하세요';
const DEFAULT_ROOM = { adult: 2, child: [] } as const;

export const GlobalSearch: FC<GlobalSearchProps> = (props: GlobalSearchProps) => {
  const { data, pageNameLog, globalPlaceFilter } = props;
  const widgetLogMeta = data?.logMeta || {};
  const items = data?.items?.length ? data.items[0].items : [];
  const search = items?.find((item) => item.type === 'SEARCH');
  const calendar = items?.find((item) => item.type === 'CALENDAR');
  const rooms = items?.find((item) => item.type === 'ROOMS');
  const { ref, inView } = useInView({ triggerOnce: true });

  useEffect(() => {
    if (inView) {
      clientLogStandardized('impression', pageNameLog, data?.logMeta);
    }
  }, [inView, data]);

  const checkInOutText = useMemo(() => {
    const today = new Date();
    const checkIn = globalPlaceFilter?.checkinDate || format(today, 'yyyy-MM-dd');
    const checkOut = globalPlaceFilter?.checkoutDate || format(addDays(today, 1), 'yyyy-MM-dd');
    const period = differenceInDays(checkOut, checkIn);

    return `${format(checkIn, 'MM.dd')}~${format(checkOut, 'MM.dd')}${period >= 1 ? ` · ${period}박` : ''}`;
  }, [globalPlaceFilter]);

  const roomAndGuestText = useMemo(() => {
    let adults = 0;
    let children = 0;
    const placeFilterRooms = globalPlaceFilter ? globalPlaceFilter.rooms : [DEFAULT_ROOM];

    placeFilterRooms.forEach((room) => {
      adults += room.adult;
      children += (room.child || []).length;
    });

    return `객실 ${placeFilterRooms.length}, 성인 ${adults || 2}, 아동 ${children}`;
  }, [globalPlaceFilter]);

  const handleClick = async (item?: {
    action?: GlobalSearchResponseItemType['action'];
    logMeta?: GlobalSearchResponseItemType['logMeta'];
  }) => {
    const { action, logMeta } = item || {};
    await waitUntilClickTimeout(clientLogStandardized('click', pageNameLog, { ...widgetLogMeta, ...logMeta }));
    if (action) {
      navigateTo(action);
    }
  };

  return (
    <div ref={ref} className="w-full pc:max-w-pc-size">
      <GlobalSearchMobile
        placeholder={search?.placeholder || DEFAULT_PLACEHOLDER}
        checkInOutText={checkInOutText}
        roomAndGuestText={roomAndGuestText}
        onClickSearch={() => handleClick(search)}
        onClickCalendar={() => handleClick(calendar)}
        onClickRooms={() => handleClick(rooms)}
      />
      <GlobalSearchPC
        title={data?.header?.title || '어디로 떠날까요?'} // [YAFE-2320] 어드민에 아직 title 을 셋팅할 수 있는 기능이 없어서 기본값 지정
        placeholder={search?.placeholder || DEFAULT_PLACEHOLDER}
        checkInOutText={checkInOutText}
        roomAndGuestText={roomAndGuestText}
        onClickSearch={() => handleClick(search)}
        onClickCalendar={() => handleClick(calendar)}
        onClickRooms={() => handleClick(rooms)}
      />
    </div>
  );
};
